import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const SCOPE_HELP = {
  personal: "Just your own numbers.",
  team: "Your entire authorised downline, including your personal data — one aggregate total.",
  all: "Every ATL partner (admin only).",
  branch: "The whole named branch, aggregated.",
};

export const monthKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
};

/**
 * Fetch & memoise /scope/options for the current actor. Backend returns:
 *   { personal, team, all, team_label, branches:[{id,label,...}] }
 * - partner → Personal only
 * - leader/team_leader/platinum/oversight → Personal + Team
 * - admin → Personal + "My Whole Team / Organisation" + All ATL
 *   + 6 fixed named team branches
 */
export function useScopeOptions() {
  const [data, setData] = useState({
    personal: true, team: false, all: false,
    team_label: "Team (you + downline)",
    branches: [],
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/scope/options")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const options = useMemo(() => {
    const out = [{ value: "personal", label: "Personal" }];
    if (data.team) out.push({ value: "team", label: data.team_label || "Team (you + downline)" });
    if (data.all) out.push({ value: "all", label: "All ATL" });
    for (const b of data.branches || []) {
      out.push({
        value: `branch:${b.id}`,
        label: b.label || b.name || "Branch",
        group: "branches",
      });
    }
    return out;
  }, [data]);
  return { options, loading, raw: data };
}

/** Dynamic scope picker — Personal / Team / All ATL / named branches. */
export default function ScopePicker({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-56" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Month/year dropdown — last `window` months, descending. */
export function MonthPicker({ value, onChange, window = 24, testid }) {
  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < window; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(monthKey(d));
    }
    return out;
  }, [window]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-44" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {months.map((m) => (
          <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
